import { getResourceTypeDetails, ResourceType, ResourceVariant } from 'src/utils/resource-types'
import { ProcessingStatusDto } from '../services/dtos'
import { Arn } from 'src/utils/types'

export function maxDate(l: Date, r: Date) {
  if (l < r) return r
  return l
}

export function minDate(l: Date, r: Date) {
  if (l < r) return l
  return r
}

export function maxOrAnyDate(l: Date | undefined, r: Date | undefined): Date | null {
  /**
   * Like the maxDate function, but handles undefined and nulls better, will return
   * null if both dates are null (or undefined).
   */
  if (l && r) {
    return maxDate(l, r)
  } else if (l) {
    return l
  } else if (r) {
    return r
  } else {
    return null
  }
}

export function isLaterThan(l: Date | undefined, r: Date | undefined): boolean {
  // Returns True if the first Date is later than the second date, or
  // the second date is null. Otherwise, it returns false.
  if (l && r) {
    return l > r
  } else if (!l) {
    // l is empty/null, so can't be later than r.
    return false
  } else {
    // r must not be null.
    return true
  }
}

export function dateDiff(d2: Date, d1: Date) {
  return d2.valueOf() - d1.valueOf()
}

export function getResourceTypeDisplayString(resourceType: ResourceType, resourceVariant: ResourceVariant) {
  return getResourceTypeDetails({ resourceType: resourceType, resourceVariant: resourceVariant }).displayName
}

export function getResourceTypeDisplayStringPlural(resourceType: ResourceType, resourceVariant: ResourceVariant) {
  return getResourceTypeDetails({ resourceType: resourceType, resourceVariant: resourceVariant }).pluralDisplayName
}

/** This function will receive Arn as string and return a name from it */
export function getResourceNameFromArn(arn: Arn) {
  if (!arn) return
  // Split the ARN by colon
  const parts = arn.split(':')

  // Join the parts from the 6th element onwards back into a string
  const result = parts.slice(5).join(':')
  return result
}

export function resourceTypeShouldRender(resourceType: ResourceType) {
  return getResourceTypeDetails({ resourceType: resourceType }).displayForSelection
}

// True if this should be shown in the resource selection view
export function resourceTypeAndVariantShouldRender(type: ResourceType, variant: ResourceVariant) {
  return getResourceTypeDetails({ resourceType: type, resourceVariant: variant }).displayForSelection
}

// True if this should be shown in the resource status page
export function resourceTypeAndVariantVisibleInStatusPage(type: ResourceType, variant: ResourceVariant) {
  // TODO - Change sortOrder < 100 to a proper display field.
  return getResourceTypeDetails({ resourceType: type, resourceVariant: variant }).displayInStatus
}

/**
 * Computes a display name for a resource, combining the friendly name and ID.
 * Shows the full name without truncation
 */
export function computeResourceDisplayName(resourceId: string, resourceName: string, resourceDisplayName?: string) {
  if (resourceDisplayName) {
    return resourceDisplayName
  }

  if (resourceId === resourceName || !resourceName || resourceName.length === 0) {
    return resourceId
  }

  return `${resourceName} (${resourceId})`
}

function computeMsAge(date: Date, now: Date = null) {
  if (now === null) now = new Date()

  if (!date) {
    date = null
  }

  return now.valueOf() - date.valueOf()
}

export function timeAgoString(date: Date) {
  let secondAge = Math.round(computeMsAge(date) / 1000)
  return `${durationText(secondAge)} ago`
}

export function durationText(seconds: number, precise: boolean = false, precision: number = 3) {
  if (seconds < 60) {
    if (precise) {
      return `${seconds.toPrecision(precision)} seconds`
    }
    return 'seconds'
  }
  if (seconds < 60 * 60) {
    // within the first hour
    // express minutes with no decimal places
    let mins = Math.round(seconds / 60)
    if (mins === 1) {
      return '1 min'
    }
    return `${mins} mins`
  }
  if (seconds < 60 * 60 * 10) {
    // within the first 10 hours
    // express hours with 1 decimal place
    let hourTenths = Math.round(seconds / 60 / 6)
    if (hourTenths === 10) {
      return '1 hour'
    }
    return `${hourTenths / 10} hours`
  }
  if (seconds < 60 * 60 * 48) {
    // within the first 2 days
    // express hours with 0 decimal places
    return `${Math.round(seconds / 60 / 60)} hours`
  }
  // express as days when over 48 hours
  if (seconds < 60 * 60 * 24 * 7) {
    // within the first 7 days
    // express days with 1 decimal place
    let dayTenths = Math.round(seconds / 60 / 60 / 2.4)
    return `${dayTenths / 10} days`
  }
  // express days with 0 decimal places
  return `${Math.round(seconds / 60 / 60 / 24)} days`
}

export function shortTimeString(date: Date) {
  function sameDate(d1: Date, d2: Date) {
    // evaluates whether 2 Date/Time objects occur on the same date in local timezone.
    return d1.getFullYear() == d2.getFullYear() && d1.getMonth() == d2.getMonth() && d1.getDate() == d2.getDate()
  }
  try {
    let now = new Date()
    if (sameDate(now, date)) {
      // same date, so only return the time in reasonably short format
      return date.toLocaleTimeString([], { timeZoneName: 'short', minute: 'numeric', hour: 'numeric' })
    }
    return date.toLocaleString([], {
      timeZoneName: 'short',
      minute: 'numeric',
      hour: 'numeric',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    })
  } catch (err) {
    // ignore the error
  }
  // If the browser doesn't support the options parameter, just return the big ugly string.
  return date.toLocaleTimeString()
}

export function equalSets<T>(a: Set<T>, b: Set<T>) {
  return a.size === b.size && new Set([...a, ...b]).size === a.size
}

export function diffSets<T>(a: Set<T>, b: Set<T>) {
  return new Set(Array.from(a).filter((x) => !b.has(x)))
}

export function intersectionSets<T>(a: Set<T>, b: Set<T>) {
  return new Set(Array.from(a).filter((x) => b.has(x)))
}

export function mapToObject(map: Map<string, any>) {
  let obj = {}
  map.forEach((value, key) => {
    obj[key] = value
  })
  return obj
}

export function processingIsActive(status: ProcessingStatusDto = null, request_timestamp: Date = null): boolean {
  if (status) {
    const latest_start = maxOrAnyDate(maxOrAnyDate(request_timestamp, status.lastRequest), status.lastStart)
    const latest_finish = maxOrAnyDate(status.lastSuccess, status.lastFailure)
    return status.active || latest_start > latest_finish
  } else {
    return false
  }
}

export function processingCompleteTime(status: ProcessingStatusDto) {
  return maxOrAnyDate(status.lastFailure, status.lastSuccess)
}

export function validOctet(octet: string) {
  if (!octet) {
    return false
  }
  if ([...octet].every((c) => '0123456789'.includes(c))) {
    return parseInt(octet) <= 255
  } else {
    return false
  }
}

export function validCidr(cidr: string) {
  const x = cidr.split('/')
  if (x.length > 2) {
    return false
  } // Can only have one '/' in a CIDR.
  const octets = x[0].split('.')
  if (octets.length != 4) {
    return false
  }
  const validMask = x.length > 1 ? validOctet(x[1]) : true
  return octets.every(validOctet) && validMask
}

const DOMAIN_REGEX = /^(?!-)\.?([A-Za-z0-9-]{1,63}(?<!-)\.)+[A-Za-z]{2,6}$/

export function validDomain(domain: string) {
  return DOMAIN_REGEX.test(domain)
}
/**
 * Calculates the scrollable height as a percentage of the viewport (`vh`),
 * it considers header & footer height.
 *
 * @param {number} headerFooterHeight - Height of header and footer in pixels (default: 397).
 * @returns {string} - The scroll height in `vh`, rounded.
 */
export function calculateScrollHeight(headerFooterHeight: number = 397): string {
  const viewportHeight = window.innerHeight // Viewport height in pixels
  const adjustedHeight = viewportHeight - headerFooterHeight // Adjusted height in pixels
  const adjustedHeightVh = (adjustedHeight / viewportHeight) * 100
  return `${Math.round(adjustedHeightVh)}vh`
}

export function areDeeplyEqual(obj1, obj2) {
  if (obj1 === obj2) {
    return true
  }

  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) {
      return false
    }

    return obj1.every((elem, index) => areDeeplyEqual(elem, obj2[index]))
  }

  if (typeof obj1 === 'object' && typeof obj2 === 'object' && obj1 !== null && obj2 !== null) {
    if (Array.isArray(obj1) || Array.isArray(obj2)) {
      return false
    }

    const keys1 = Object.keys(obj1)
    const keys2 = Object.keys(obj2)

    if (keys1.length !== keys2.length || !keys1.every((key) => keys2.includes(key))) {
      return false
    }

    for (let key in obj1) {
      if (!areDeeplyEqual(obj1[key], obj2[key])) {
        return false
      }
    }

    return true
  }

  return false
}
